// file: `react/src/components/ChatSocketContext.tsx`:

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { AUTH_TOKEN_CHANGED_EVENT, hasSession } from '../utils/authToken.ts';
import { webSocketURL } from '../utils/networkUtils';
import { useUser } from './UserContext.tsx';

type ChatEvent = Record<string, any>;
type Listener = (msg: ChatEvent) => void;
export type ConnectionStatus = 'connecting' | 'online' | 'offline';

type ChatSocketContextType = {
  online: string[];
  typing: Record<string, string[]>;
  connectionStatus: ConnectionStatus;
  send: (msg: ChatEvent) => void;
  sendTyping: (threadId: string | number) => void;
  subscribe: (fn: Listener) => () => void;
  isOnline: (shortName?: string) => boolean;
};

const ChatSocketContext = createContext<ChatSocketContextType>({
  online: [],
  typing: {},
  connectionStatus: 'offline',
  send: () => {},
  sendTyping: () => {},
  subscribe: () => () => {},
  isOnline: () => false
});

export const useChatSocket = () => useContext(ChatSocketContext);

function clearTypingTimers(timers: Record<string, ReturnType<typeof setTimeout>>) {
  Object.keys(timers).forEach((key) => clearTimeout(timers[key]));
}

export function ChatSocketProvider({ children }) {
  const { activeUser } = useUser();
  const wsRef = useRef<WebSocket | null>(null);
  const listenersRef = useRef(new Set<Listener>());
  const typingTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const lastTyping = useRef<Record<string, number>>({});
  const [online, setOnline] = useState<string[]>([]);
  const [typing, setTyping] = useState<Record<string, string[]>>({});
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('offline');

  const subscribe = useCallback((fn: Listener) => {
    listenersRef.current.add(fn);
    return () => { listenersRef.current.delete(fn); };
  }, []);

  const send = useCallback((msg: ChatEvent) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  const noteTyping = useCallback((threadId: string, shortName: string) => {
    const key = `${threadId}:${shortName}`;
    if (typingTimers.current[key]) clearTimeout(typingTimers.current[key]);
    setTyping((prev) => {
      const list = prev[threadId] || [];
      if (list.includes(shortName)) return prev;
      return { ...prev, [threadId]: [...list, shortName] };
    });
    typingTimers.current[key] = setTimeout(() => {
      setTyping((prev) => ({
        ...prev,
        [threadId]: (prev[threadId] || []).filter((name) => name !== shortName)
      }));
    }, 3200);
  }, []);

  const clearTyping = useCallback((threadId: string, shortName: string) => {
    const key = `${threadId}:${shortName}`;
    if (typingTimers.current[key]) clearTimeout(typingTimers.current[key]);
    setTyping((prev) => ({
      ...prev,
      [threadId]: (prev[threadId] || []).filter((name) => name !== shortName)
    }));
  }, []);

  const resetLiveState = useCallback(() => {
    clearTypingTimers(typingTimers.current);
    typingTimers.current = {};
    setOnline([]);
    setTyping({});
  }, []);

  const sendTyping = useCallback((threadId: string | number) => {
    const id = String(threadId);
    const now = Date.now();
    if (now - (lastTyping.current[id] || 0) < 1400) return;
    lastTyping.current[id] = now;
    send({ type: 'typing', threadId: id });
  }, [send]);

  useEffect(() => {
    if (!activeUser?.shortName || !hasSession()) {
      setConnectionStatus('offline');
      resetLiveState();
      return;
    }

    let stopped = false;
    let retry: ReturnType<typeof setTimeout> | undefined;

    const closeCurrentSocket = () => {
      if (retry) {
        clearTimeout(retry);
        retry = undefined;
      }
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };

    const connect = () => {
      if (stopped) return;
      if (!hasSession()) {
        setConnectionStatus('offline');
        resetLiveState();
        return;
      }
      const url = webSocketURL();
      if (!url) {
        setConnectionStatus('offline');
        resetLiveState();
        return;
      }

      setConnectionStatus('connecting');
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (stopped) return;
        setConnectionStatus('online');
        ws.send(JSON.stringify({ type: 'hello' }));
        listenersRef.current.forEach((fn) => fn({ type: 'socket-open' }));
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'presence' && Array.isArray(msg.online)) {
            setOnline(msg.online.map(String));
          } else if (msg.type === 'typing' && msg.threadId && msg.shortName) {
            noteTyping(String(msg.threadId), String(msg.shortName));
          } else if (msg.type === 'typing-stop' && msg.threadId && msg.shortName) {
            clearTyping(String(msg.threadId), String(msg.shortName));
          }
          listenersRef.current.forEach((fn) => fn(msg));
        } catch (_) {
          // ignore non-JSON frames
        }
      };

      ws.onerror = (error) => {
        console.error('ChatSocket: WebSocket error:', error);
      };

      ws.onclose = () => {
        wsRef.current = null;
        resetLiveState();
        if (stopped) {
          setConnectionStatus('offline');
          return;
        }
        setConnectionStatus('offline');
        retry = setTimeout(connect, 2000);
      };
    };

    const onTokenChanged = () => {
      closeCurrentSocket();
      connect();
    };

    window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, onTokenChanged);
    connect();

    return () => {
      stopped = true;
      window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, onTokenChanged);
      closeCurrentSocket();
      resetLiveState();
      setConnectionStatus('offline');
    };
  }, [activeUser?.shortName, clearTyping, noteTyping, resetLiveState]);

  const isOnline = useCallback((shortName?: string) => {
    if (!shortName) return false;
    return online.includes(shortName);
  }, [online]);

  const value = useMemo(() => ({
    online,
    typing,
    connectionStatus,
    send,
    sendTyping,
    subscribe,
    isOnline
  }), [connectionStatus, isOnline, online, send, sendTyping, subscribe, typing]);

  return (
    <ChatSocketContext.Provider value={value}>
      {children}
    </ChatSocketContext.Provider>
  );
}
